import React, { useState, useEffect, useMemo } from "react";
import { GripVertical, FileText, Plus, X, Sparkles } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const Sidebar = ({
  questions,
  activeQuestionId,
  setActiveQuestionId,
  onAddQuestion,
  onDeleteQuestion,
  onReorderQuestions,
  sidebarWidth = 280,
  onCloseSidebar,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const questionIds = useMemo(() => questions.map((q) => q.id), [questions]);
  // Add this new state to track newly added questions
  const [newlyAddedIds, setNewlyAddedIds] = useState([]);
  // Add this to track the highest question ID we've seen
  const [highestSeenId, setHighestSeenId] = useState(null);

  useEffect(() => {
    if (questionIds.length > 0) {
      setHighestSeenId(Math.max(...questionIds));
    }
  }, [questionIds]);

  // Track window size for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (questions.length > 0) {
      const questionIds = questions.map((q) => q.id);
      const currentHighestId = Math.max(...questionIds);
      // Only trigger for actual NEW questions, not just updates to existing ones
      if (
        highestSeenId !== null &&
        currentHighestId > highestSeenId &&
        // Make sure the highest ID isn't just from updating components
        questionIds.some((id) => id > highestSeenId)
      ) {
        // Calculate which questions are new (any with ID higher than our previous highest)
        const newIds = questionIds.filter((id) => id > highestSeenId);

        // Update our tracking states
        setNewlyAddedIds((prev) => [...prev, ...newIds]);
        setHighestSeenId(currentHighestId);

        // Auto-select the highest new ID (the most recently added question)
        if (newIds.length > 0) {
          // Only set active question if it's actually a new question
          // and not just a component update
          setActiveQuestionId(currentHighestId);
        }

        // Remove the "new" status after 5 seconds
        setTimeout(() => {
          setNewlyAddedIds((prev) => prev.filter((id) => !newIds.includes(id)));
        }, 5000);
      }
    } else if (highestSeenId === 0) {
      // Initialize on first load with no questions
      setHighestSeenId(0);
    }
  }, [questions, highestSeenId, setActiveQuestionId]);

  // dnd-kit sensors: pointer + touch with small activation constraints to avoid
  // accidental drags while scrolling on mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 5 },
    })
  );

  const handleDndEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = questions.findIndex((q) => q.id === active.id);
    const newIndex = questions.findIndex((q) => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(questions, oldIndex, newIndex);
    onReorderQuestions(newOrder);
  };

  const isMobile = windowWidth < 768;
  const isCompact = windowWidth < 1024;

  // Add CSS for subtle pulse animation and hide scrollbars
  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      @keyframes pulse-light {
        0%, 100% { background-color: rgba(59, 130, 246, 0.05); }
        50% { background-color: rgba(59, 130, 246, 0.2); }
      }
      .animate-pulse-light {
        animation: pulse-light 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
      .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      .hide-scrollbar::-webkit-scrollbar { display: none; }
      .touch-scroll { -webkit-overflow-scrolling: touch; touch-action: pan-y; }
      /* Sleek sidebar scrollbar */
      .sidebar-scroll { scrollbar-width: thin; scrollbar-color: rgba(100,116,139,0.4) transparent; }
      .sidebar-scroll::-webkit-scrollbar { width: 8px; }
      .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
      .sidebar-scroll::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #e5e7eb, #cbd5e1); border-radius: 9999px; }
      .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    `;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);

  // Sortable item for questions list
  const SortableQuestionRow = ({ q, isCompact, isNewlyAdded, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: q.id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 1 : undefined,
    };

    const isActive = q.id === activeQuestionId;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`group relative rounded-lg transition-all duration-150 w-full border cursor-grab active:cursor-grabbing ${
          isActive
            ? "bg-blue-50 ring-2 ring-blue-400"
            : "bg-white hover:bg-gray-50 hover:border-gray-200"
        } ${isNewlyAdded && !isActive ? "animate-pulse-light" : ""}`}
        {...attributes}
        {...listeners}
      >
        {isNewlyAdded && (
          <div className="absolute -right-1 -top-1 text-blue-500 animate-pulse z-10">
            <Sparkles size={16} />
          </div>
        )}

        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
          <GripVertical size={16} />
        </div>

        <button
          onClick={() => setActiveQuestionId(q.id)}
          className={`w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-lg ${
            isCompact ? "p-2 pl-9" : "md:p-3 md:pl-10"
          }`}
        >
          <div
            className={`flex items-start ${isCompact ? "gap-2.5" : "md:gap-3"}`}
          >
            <div
              className={`flex-shrink-0 rounded-md bg-gray-100 overflow-hidden ${
                isCompact ? "w-9 h-9" : "md:w-10 md:h-10 lg:w-12 lg:h-12"
              }`}
            >
              {q.components?.find((c) => c.type === "image_upload" && c.image)
                ?.image ? (
                <img
                  src={
                    q.components.find(
                      (c) => c.type === "image_upload" && c.image
                    ).image
                  }
                  alt={`Question ${q.id}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <FileText size={isCompact ? 14 : 18} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p
                className={`font-medium truncate ${
                  isCompact ? "text-xs" : "md:text-sm"
                } ${isActive ? "text-blue-600" : "text-gray-700"}`}
              >
                {`Question ${index + 1}`}
              </p>
              <p
                className={`${
                  isCompact ? "text-[10px]" : "md:text-xs"
                } text-gray-500`}
              >
                {q.components?.length || 0} components
              </p>
            </div>
          </div>
        </button>

        {questions.length > 1 && (
          <button
            onPointerDownCapture={(e) => e.stopPropagation()}
            onMouseDownCapture={(e) => e.stopPropagation()}
            onTouchStartCapture={(e) => e.stopPropagation()}
            onClick={() => onDeleteQuestion(q.id)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-red-50 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X size={isCompact ? 12 : 14} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      className="h-full flex flex-col border-r border-gray-200 bg-white"
      style={{ width: isMobile ? "100%" : `${sidebarWidth}px` }}
    >
      <div className="px-2 md:px-3 lg:px-4 py-1.5 lg:py-2 border-b w-full flex items-center justify-between">
        <div className="flex items-center gap-1 md:gap-2 min-w-0">
          <h2
            className={`font-semibold text-gray-800 tracking-wide truncate ${
              isCompact ? "text-[11px]" : "md:text-xs lg:text-sm"
            }`}
          >
            Questions
          </h2>
          <span
            className={`text-gray-400 whitespace-nowrap ${
              isCompact ? "text-[10px]" : "md:text-xs"
            }`}
          >
            {questions.length}
          </span>
        </div>
        <button
          onClick={() => onCloseSidebar && onCloseSidebar()}
          className={`inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 ${
            isCompact ? "w-6 h-6" : "md:w-7 md:h-7 lg:w-8 lg:h-8"
          }`}
          aria-label="Close sidebar"
        >
          <X size={isCompact ? 14 : 16} />
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDndEnd}
      >
        <SortableContext
          items={questions.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          <div
            className={`flex-1 overflow-y-auto overflow-x-hidden pb-14 space-y-2 w-full relative touch-scroll sidebar-scroll ${
              isCompact ? "p-2" : "md:p-3 md:pb-14"
            }`}
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
            }}
          >
            {questions.map((q, index) => (
              <SortableQuestionRow
                key={q.id}
                index={index}
                q={q}
                isCompact={isCompact}
                isNewlyAdded={newlyAddedIds.includes(q.id)}
              />
            ))}

            <button
              onClick={onAddQuestion}
              className="sticky bg-white bottom-0 w-full mt-3 p-3 rounded-lg border border-dashed border-gray-300 text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Plus size={isMobile ? 16 : 18} />
              <span>Add Question</span>
            </button>
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default Sidebar;
